import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import CalibrationSetTracker from '../workout/calibration/CalibrationSetTracker'
import CalibrationCardioTracker from '../workout/calibration/CalibrationCardioTracker'
import { KILO } from '../../constants/KILO_THEME'
import type { CalibrationDayEntry } from '../../lib/calibration/calendarLayout'
import { CALIBRATION_SESSION_SEQUENCE } from '../../lib/calibration/calendarLayout'
import {
  defaultCompoundLiftsForSession,
  finalizeCalibrationSession,
  metricsFromSetSession,
  suggestedCalibrationWorkingWeight,
} from '../../lib/calibration/finalizeCalibrationSession'
import { loadCalibrationProfile, type CalibrationProfile } from '../../lib/calibration/loadCalibrationProfile'
import { reconcileCalibrationState } from '../../lib/calibration/reconcileCalibrationState'
import { safeCalibrationSessionCount } from '../../lib/calibration/safeCalibrationSessionCount'
import { isCalibrationComplete } from '../../lib/calibrationTypes'
import type { CalibrationSessionType } from '../../lib/calibrationTypes'
import { useCalibrationCalendar } from '../../lib/calibration/useCalibrationCalendar'
import type { CalibrationSetResult } from '../../lib/calibration/tempoConstants'
import { useCalibrationSessionCopy } from '../../hooks/useCalibrationSessionCopy'

type CoachGatePhase = 'dashboard' | 'session' | 'transition'

type CalibrationCoachGateProps = {
  userId: string | null
  bodyWeightKg?: number | null
  onProtocolRefresh?: () => void
  onOpenShopping?: () => void
  children: ReactNode
}

const SET_TRACKER_SESSIONS: Set<CalibrationSessionType> = new Set([
  'upper_compound',
  'lower_compound',
  'isolation_density',
])

const CalibrationTransitionOverlay = ({
  onDone,
  error,
}: {
  onDone: () => void
  error: string | null
}) => {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-kilo-page px-6">
      <div className="max-w-sm text-center">
        {error ? (
          <>
            <p className="text-lg font-bold text-red-400">{t('workout.calibration.transitionPaused')}</p>
            <p className="mt-2 text-sm text-kilo-muted">{error}</p>
            <button
              type="button"
              className="mt-6 rounded-2xl bg-kilo-nested px-6 py-3 text-sm font-semibold text-kilo-text"
              onClick={onDone}
            >
              {t('workout.calibration.returnToCoach')}
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-kilo-accent border-t-transparent" />
            <p className="text-xl font-black uppercase tracking-wide text-kilo-text">
              {t('workout.calibration.activating')}
            </p>
            <p className="mt-2 text-sm text-kilo-muted">{t('workout.calibration.loadingWeek3')}</p>
          </>
        )}
      </div>
    </div>
  )
}

/** Shown for ~3 s after sessions 1–3 complete, before returning to dashboard. */
const CalibrationSessionCompleteOverlay = ({
  sessionNumber,
  onDismiss,
}: {
  sessionNumber: number
  onDismiss: () => void
}) => {
  const { t } = useTranslation()
  const isFinal = sessionNumber >= 4

  const titleKey =
    sessionNumber >= 4
      ? 'workout.calibration.sessionCompleteTitle_final'
      : sessionNumber === 1
        ? 'workout.calibration.sessionCompleteTitle_movement'
        : sessionNumber === 2
          ? 'workout.calibration.sessionCompleteTitle_strength'
          : 'workout.calibration.sessionCompleteTitle_recovery'

  const bodyKey = isFinal
    ? 'workout.calibration.sessionCompleteBody_final'
    : sessionNumber === 1
      ? 'workout.calibration.sessionCompleteBody_movement'
      : sessionNumber === 2
        ? 'workout.calibration.sessionCompleteBody_strength'
        : 'workout.calibration.sessionCompleteBody_recovery'

  useEffect(() => {
    if (isFinal) return
    const timer = setTimeout(onDismiss, 3000)
    return () => clearTimeout(timer)
  }, [isFinal, onDismiss])

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-kilo-page/95 px-6 text-center backdrop-blur-sm">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-kilo-accent/20">
        <MaterialIcon name="check" size="lg" className="text-kilo-accent" />
      </div>
      <p className="text-lg font-semibold text-kilo-text">{t(titleKey)}</p>
      <p className="mt-2 max-w-xs text-sm text-kilo-muted">{t(bodyKey)}</p>
      {isFinal ? (
        <button
          type="button"
          className="mt-6 rounded-2xl bg-kilo-accent px-6 py-3 text-sm font-bold text-black"
          onClick={onDismiss}
        >
          {t('workout.calibration.sessionCompleteCta_final')}
        </button>
      ) : (
        <div className="mt-5 flex gap-1.5">
          {([1, 2, 3, 4] as const).map((n) => (
            <div
              key={n}
              className={`h-1.5 w-6 rounded-full ${n <= sessionNumber ? 'bg-kilo-accent' : 'bg-kilo-border'}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const CalibrationFeatureDay = ({
  entry,
  onOpenShopping,
}: {
  entry: CalibrationDayEntry
  onOpenShopping?: () => void
}) => {
  const { t } = useTranslation()

  return (
    <BentoCard gradient="slate" featured className="mb-4">
      <p className={KILO.meta}>{t('workout.calibration.today')}</p>
      <h2 className="mt-1 text-xl font-bold text-kilo-text">{entry.title}</h2>
      <p className={`mt-2 text-sm ${KILO.text.muted}`}>{entry.subtitle}</p>
      <div className="mt-4 flex flex-col gap-2">
        {entry.featureCards.includes('readiness_check') ? (
          <p className={`rounded-xl border border-kilo-border bg-kilo-card px-4 py-3 text-sm ${KILO.text.muted}`}>
            {t('workout.calibration.readinessHint')}
          </p>
        ) : null}
        {entry.featureCards.includes('grocery_matcher') ? (
          <button
            type="button"
            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-text"
            onClick={onOpenShopping}
          >
            <MaterialIcon name="shopping_cart" size="sm" />
            {t('workout.calibration.openGrocery')}
          </button>
        ) : null}
      </div>
    </BentoCard>
  )
}

/** Shown the first time a user lands on the Coach tab with calibration_completed_sessions === 0. */
const CalibrationExplainCard = () => {
  const { t } = useTranslation()

  return (
    <div className="mx-0 mb-4 rounded-2xl border border-green-900/30 bg-green-950/20 px-4 py-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-green-400">
        {t('workout.calibration.whyMeta')}
      </p>
      <p className="mt-2 text-sm font-semibold text-kilo-text">
        {t('workout.calibration.whyHeading')}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-kilo-muted">
        {t('workout.calibration.whyBody')}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex gap-1">
          {([1, 2, 3, 4] as const).map((n) => (
            <div key={n} className="h-1.5 w-6 rounded-full bg-kilo-border" />
          ))}
        </div>
        <span className="text-[10px] text-kilo-muted">
          {t('workout.calibration.whyDuration')}
        </span>
      </div>
    </div>
  )
}

/** Shown on non-session days when calibration_completed_sessions is 1–3. */
const CalibrationPickUpBanner = ({ sessionsCompleted }: { sessionsCompleted: number }) => {
  const { t } = useTranslation()
  const { sessionCopy } = useCalibrationSessionCopy()
  const bodyKey =
    sessionsCompleted === 1
      ? 'workout.calibration.pickingUpBody_1'
      : sessionsCompleted === 2
        ? 'workout.calibration.pickingUpBody_2'
        : 'workout.calibration.pickingUpBody_3'

  const completedLabels = CALIBRATION_SESSION_SEQUENCE.slice(0, sessionsCompleted).map(
    (type) => sessionCopy[type].title,
  )

  return (
    <div className="mb-4 rounded-xl border border-kilo-border bg-kilo-nested px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-kilo-muted">
        {t('workout.calibration.pickingUpMeta')}
      </p>
      <p className="mt-1 text-xs text-kilo-muted">
        {t('workout.calibration.sessionsCompletedOf', { completed: sessionsCompleted })}
      </p>
      {completedLabels.length > 0 ? (
        <p className="mt-2 text-xs text-kilo-text">
          <span className="font-semibold text-kilo-muted">
            {t('workout.calibration.completedSessionsLabel')}:{' '}
          </span>
          {completedLabels.join(' · ')}
        </p>
      ) : null}
      <p className="mt-2 text-xs text-kilo-muted">{t(bodyKey)}</p>
    </div>
  )
}

const CalibrationSessionPrompt = ({
  entry,
  sessionsCompleted,
  onStart,
}: {
  entry: CalibrationDayEntry
  sessionsCompleted: number
  onStart: () => void
}) => {
  const { t } = useTranslation()
  const { sessionCopy } = useCalibrationSessionCopy()
  const session = entry.session!
  const copy = sessionCopy[session.sessionType]
  const sessionNumber = session.sessionNumber
  const completedLabels = CALIBRATION_SESSION_SEQUENCE.slice(0, sessionsCompleted).map(
    (type) => sessionCopy[type].title,
  )

  return (
    <BentoCard gradient="forest" featured className="mb-4">
      <div className="flex items-center justify-between">
        <p className={KILO.meta}>
          {t('workout.calibration.sessionOf', { current: sessionNumber })}
        </p>
        <div className="flex gap-1.5">
          {([1, 2, 3, 4] as const).map((n) => (
            <div
              key={n}
              className={`h-2 w-2 rounded-full transition-all ${
                n <= sessionsCompleted
                  ? 'bg-kilo-accent'
                  : n === sessionNumber
                    ? 'animate-pulse bg-kilo-accent opacity-70'
                    : 'bg-kilo-border'
              }`}
            />
          ))}
        </div>
      </div>
      {sessionsCompleted > 0 ? (
        <p className="mt-2 text-xs text-kilo-muted">
          {t('workout.calibration.sessionsCompletedOf', { completed: sessionsCompleted })}
          {completedLabels.length > 0 ? `: ${completedLabels.join(' · ')}` : ''}
        </p>
      ) : null}
      <h2 className="mt-1 text-xl font-bold text-kilo-text">{copy.title}</h2>
      <p className={`mt-2 text-sm ${KILO.text.muted}`}>{copy.subtitle}</p>
      <button
        type="button"
        className="group mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black"
        onClick={onStart}
      >
        <MaterialIcon name="play_arrow" hoverable size="sm" />
        {t('workout.calibration.startSession')}
      </button>
    </BentoCard>
  )
}

const CalibrationCardioComplete = ({
  sessionType,
  onComplete,
  busy,
  error,
}: {
  sessionType: CalibrationSessionType
  onComplete: () => void
  busy: boolean
  error: string | null
}) => {
  const { sessionCopy } = useCalibrationSessionCopy()
  const copy = sessionCopy[sessionType]

  return (
    <CalibrationCardioTracker
      title={copy.title}
      subtitle={copy.subtitle}
      onComplete={onComplete}
      busy={busy}
      error={error}
    />
  )
}

const WorkoutSectionPlaceholder = () => (
  <BentoCard gradient="none" className="!p-0 overflow-hidden">
    <div className="flex animate-pulse items-center gap-3 px-5 py-4" aria-hidden>
      <div className="h-5 w-5 shrink-0 rounded bg-kilo-nested" />
      <div className="min-w-0 flex-1">
        <div className="h-3 w-16 rounded bg-kilo-nested" />
        <div className="mt-2 h-4 w-52 max-w-full rounded bg-kilo-nested" />
      </div>
    </div>
  </BentoCard>
)

const CalibrationCoachGate = ({
  userId,
  bodyWeightKg,
  onProtocolRefresh,
  onOpenShopping,
  children,
}: CalibrationCoachGateProps) => {
  const { t } = useTranslation()
  const { sessionCopy } = useCalibrationSessionCopy()
  const [profile, setProfile] = useState<CalibrationProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [phase, setPhase] = useState<CoachGatePhase>('dashboard')
  const [busy, setBusy] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [transitionError, setTransitionError] = useState<string | null>(null)
  const [completedSessionN, setCompletedSessionN] = useState<number | null>(null)

  const refreshProfile = useCallback(async () => {
    if (!userId) {
      setProfileLoading(false)
      return
    }
    setProfileLoading(true)
    const { error: reconcileError } = await reconcileCalibrationState(userId)
    if (reconcileError) {
      setProfileError(reconcileError)
      setProfileLoading(false)
      return
    }
    const { profile: loaded, error } = await loadCalibrationProfile(userId)
    setProfile(loaded)
    setProfileError(error)
    setProfileLoading(false)
  }, [userId])

  useEffect(() => {
    void refreshProfile()
  }, [refreshProfile])

  const calibrationProfile = useMemo(() => {
    if (!profile) return null
    return {
      ...profile,
      calibration_completed_sessions: safeCalibrationSessionCount(profile),
    }
  }, [profile])

  const calendar = useCalibrationCalendar({
    userId,
    profile: calibrationProfile,
    disabled: !calibrationProfile || isCalibrationComplete(calibrationProfile),
  })

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const sessionsCompleted = calibrationProfile?.calibration_completed_sessions ?? 0
  const nextSession = calendar.nextPendingSession
  const nextSessionEntry = useMemo(
    () => (nextSession ? calendar.entryForDate(nextSession.assignedDate) : null),
    [calendar, nextSession],
  )
  const todayMatchesNextSession = Boolean(
    nextSession && nextSession.assignedDate === today && nextSession.status !== 'completed',
  )
  const activeSessionEntry = todayMatchesNextSession ? nextSessionEntry : null

  const handleSetComplete = useCallback(
    async (sessionType: CalibrationSessionType, result: CalibrationSetResult) => {
      if (!userId || !activeSessionEntry?.session) return

      setBusy(true)
      setSessionError(null)

      const velocityHistory = result.velocityDeviationHistory.map((v) => ({
        rep_index: v.rep_index,
        delta_ms: v.delta_ms,
        phase: v.phase,
      }))

      const finalizeResult = await finalizeCalibrationSession(
        userId,
        metricsFromSetSession(
          sessionType,
          result.tempoComplianceRate,
          velocityHistory,
          sessionType === 'isolation_density' ? Math.max(0, 100 - result.tempoComplianceRate) : null,
        ),
        {
          compoundLifts: defaultCompoundLiftsForSession(
            sessionType,
            result.repsCompleted,
            bodyWeightKg ?? null,
            result.workingWeightKg ?? null,
          ),
          finalSetRir: result.rir,
        },
      )

      setBusy(false)

      if (finalizeResult.error) {
        setSessionError(finalizeResult.error)
        return
      }

      calendar.completeSession(activeSessionEntry.session.sessionNumber, today)
      calendar.refresh()

      if (finalizeResult.transitionTriggered) {
        setPhase('transition')
        await refreshProfile()
        onProtocolRefresh?.()
        setTimeout(() => setPhase('dashboard'), 1200)
        return
      }

      if (finalizeResult.transitionError) {
        setSessionError(finalizeResult.transitionError)
        setPhase('dashboard')
        return
      }

      setCompletedSessionN(finalizeResult.sessionNumber)
      await refreshProfile()
      setPhase('dashboard')
    },
    [userId, activeSessionEntry, bodyWeightKg, calendar, refreshProfile, onProtocolRefresh, today],
  )

  const handleCardioComplete = useCallback(async () => {
    if (!userId || !activeSessionEntry?.session) return
    setBusy(true)
    setSessionError(null)

    const finalizeResult = await finalizeCalibrationSession(userId, {
      sessionType: 'cardio_efficiency',
      cardioPaceHrRatio: 0.08,
      tempoComplianceRate: null,
    })

    setBusy(false)

    if (finalizeResult.error) {
      setSessionError(finalizeResult.error)
      return
    }

    calendar.completeSession(activeSessionEntry.session.sessionNumber, today)
    calendar.refresh()

    if (finalizeResult.transitionTriggered) {
      setPhase('transition')
      await refreshProfile()
      onProtocolRefresh?.()
      setTimeout(() => setPhase('dashboard'), 1200)
      return
    }

    if (finalizeResult.transitionError) {
      setSessionError(finalizeResult.transitionError)
    }

    setCompletedSessionN(finalizeResult.sessionNumber)
    await refreshProfile()
    setPhase('dashboard')
  }, [userId, activeSessionEntry, calendar, refreshProfile, onProtocolRefresh, today])

  if (!userId) {
    return <>{children}</>
  }

  if (profileLoading) {
    return <WorkoutSectionPlaceholder />
  }

  if (profileError || !profile) {
    return (
      <BentoCard gradient="none" className="p-4">
        <p className="text-sm text-kilo-muted">{t('app.errors.profileLoad')}</p>
        <button
          type="button"
          className="mt-3 rounded-2xl bg-kilo-nested px-4 py-2 text-sm font-semibold text-kilo-text"
          onClick={() => void refreshProfile()}
        >
          {t('app.errors.retry')}
        </button>
      </BentoCard>
    )
  }

  if (isCalibrationComplete(profile) || !calendar.isActive) {
    return <>{children}</>
  }

  if (phase === 'transition') {
    return (
      <CalibrationTransitionOverlay
        error={transitionError}
        onDone={() => {
          setTransitionError(null)
          setPhase('dashboard')
        }}
      />
    )
  }

  if (completedSessionN !== null) {
    return (
      <CalibrationSessionCompleteOverlay
        sessionNumber={completedSessionN}
        onDismiss={() => setCompletedSessionN(null)}
      />
    )
  }

  const activeSession = activeSessionEntry?.session
  const sessionType = activeSession?.sessionType
  const todayEntry = calendar.entryForDate(today)

  if (phase === 'session' && sessionType) {
    if (sessionType === 'cardio_efficiency') {
      return (
        <CalibrationCardioComplete
          sessionType={sessionType}
          onComplete={() => void handleCardioComplete()}
          busy={busy}
          error={sessionError}
        />
      )
    }

    if (SET_TRACKER_SESSIONS.has(sessionType)) {
      return (
        <CalibrationSetTracker
          exerciseName={sessionCopy[sessionType].title}
          sessionNumber={activeSession!.sessionNumber}
          targetReps={sessionType === 'isolation_density' ? 4 : 5}
          repAdvanceMode="manual"
          captureWorkingWeight={sessionType === 'upper_compound' || sessionType === 'lower_compound'}
          suggestedWorkingWeightKg={suggestedCalibrationWorkingWeight(sessionType, bodyWeightKg ?? null)}
          onSetComplete={(result) => {
            void handleSetComplete(sessionType, result)
          }}
        />
      )
    }
  }

  const isFirstSession = sessionsCompleted === 0
  const showFeatureDay = Boolean(todayEntry && !activeSession && todayEntry.featureCards.length > 0)
  const showSessionPrompt = Boolean(activeSession && activeSession.status !== 'completed')
  const showPickUpBanner = !showSessionPrompt && sessionsCompleted > 0 && sessionsCompleted < 4

  return (
    <>
      {showSessionPrompt ? (
        <>
          {isFirstSession && <CalibrationExplainCard />}
          <CalibrationSessionPrompt
            entry={activeSessionEntry!}
            sessionsCompleted={sessionsCompleted}
            onStart={() => setPhase('session')}
          />
        </>
      ) : (
        <>
          {isFirstSession && <CalibrationExplainCard />}
          {showPickUpBanner && <CalibrationPickUpBanner sessionsCompleted={sessionsCompleted} />}
          {showFeatureDay ? (
            <CalibrationFeatureDay entry={todayEntry!} onOpenShopping={onOpenShopping} />
          ) : null}
        </>
      )}
      {sessionError && phase === 'dashboard' ? (
        <p className="mt-2 text-sm text-red-400">{sessionError}</p>
      ) : null}
    </>
  )
}

export default CalibrationCoachGate
